// Compresses JPEG/PNG images client-side via Canvas API before upload.
// Non-image files (PDFs, Word docs, etc.) pass through untouched.

const COMPRESS_THRESHOLD_MB = 2;
const MAX_FILE_SIZE_MB = 10;
const MAX_DIMENSION = 1600;
const INITIAL_QUALITY = 0.95;
const MIN_QUALITY = 0.6;
const QUALITY_STEP = 0.05;
const OUTPUT_TYPE = 'image/jpeg';
const COMPRESSIBLE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

export function isCompressibleImage(file) {
  return file && COMPRESSIBLE_TYPES.includes(file.type.toLowerCase());
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to decode image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function fitDimensions(w, h, max) {
  if (w <= max && h <= max) return { width: w, height: h };
  const scale = Math.min(max / w, max / h);
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

function toBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error('toBlob failed')),
      type,
      quality
    );
  });
}

function drawToCanvas(img, w, h) {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, w, h);
  return canvas;
}

export async function compressImage(file, options = {}) {
  if (!isCompressibleImage(file)) return file;

  const thresholdBytes = (options.compressThresholdMB || COMPRESS_THRESHOLD_MB) * 1024 * 1024;
  const maxBytes = (options.maxFileSizeMB || MAX_FILE_SIZE_MB) * 1024 * 1024;
  const maxDim = options.maxDimension || MAX_DIMENSION;

  if (file.size <= thresholdBytes) return file;

  try {
    const img = await loadImage(file);
    const { width, height } = fitDimensions(img.width, img.height, maxDim);
    let canvas = drawToCanvas(img, width, height);

    // Reduce quality progressively until under maxBytes
    let quality = INITIAL_QUALITY;
    let blob = await toBlob(canvas, OUTPUT_TYPE, quality);
    while (blob.size > maxBytes && quality > MIN_QUALITY) {
      quality -= QUALITY_STEP;
      blob = await toBlob(canvas, OUTPUT_TYPE, quality);
    }

    // Last resort: shrink dimensions to 75%
    if (blob.size > maxBytes) {
      canvas = drawToCanvas(img, Math.round(width * 0.75), Math.round(height * 0.75));
      blob = await toBlob(canvas, OUTPUT_TYPE, INITIAL_QUALITY);
    }

    let name = file.name;
    if (file.type === 'image/png') name = name.replace(/\.png$/i, '.jpg');

    return new File([blob], name, { type: OUTPUT_TYPE, lastModified: Date.now() });
  } catch {
    // Compression failed — return original so the upload can still proceed
    return file;
  }
}
